import {
  CircleCheckIcon,
  Copy,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from "lucide-react";
import { useTheme } from "next-themes";
import { Toaster as Sonner, toast as rawToast, type ToasterProps } from "sonner";

// Helper function to handle robust clipboard copying
function copyToClipboard(text: string) {
  const proceed = () => {
    rawToast.success("Copiado al portapapeles", {
      duration: 2000,
    });
  };

  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(proceed).catch((err) => {
      console.error("Error direct copy:", err);
      fallbackCopy(text, proceed);
    });
  } else {
    fallbackCopy(text, proceed);
  }
}

function fallbackCopy(text: string, callback: () => void) {
  try {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const successful = document.execCommand("copy");
    document.body.removeChild(textArea);
    if (successful) {
      callback();
    }
  } catch (err) {
    console.error("Fallback copy failed", err);
  }
}

// Global intercept for toast.error to inject copy action button
const originalError = rawToast.error;
(rawToast as any).error = (message: any, data: any) => {
  let textToCopy = "";
  if (typeof message === "string") {
    textToCopy = message;
  }

  const description = data?.description;
  if (typeof description === "string") {
    if (textToCopy) {
      textToCopy = `${textToCopy}\n\nDetalle:\n${description}`;
    } else {
      textToCopy = description;
    }
  }

  const copyAction = textToCopy
    ? {
        label: (
          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/15 hover:bg-white/25 active:bg-white/35 text-white text-xs font-semibold transition-all border border-white/10 select-none cursor-pointer shadow-sm">
            <Copy className="size-3" />
            Copiar
          </span>
        ),
        onClick: (e: any) => {
          e?.preventDefault?.();
          e?.stopPropagation?.();
          copyToClipboard(textToCopy);
        },
      }
    : undefined;

  return originalError(message, {
    ...data,
    action: data?.action || copyAction,
    duration: data?.duration || 10000, // Error toasts last 10s by default to let user read/copy
  });
};

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      icons={{
        success: <CircleCheckIcon className="size-4" />,
        info: <InfoIcon className="size-4" />,
        warning: <TriangleAlertIcon className="size-4" />,
        error: <OctagonXIcon className="size-4" />,
        loading: <Loader2Icon className="size-4 animate-spin" />,
      }}
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
          "--border-radius": "var(--radius)",
        } as React.CSSProperties
      }
      {...props}
    />
  );
};

export { Toaster };
